import React, { useEffect, useRef, useState } from 'react';
import {
  IoArrowBack,
  IoChatbubbleEllipsesOutline,
  IoCheckmarkCircle,
  IoCloseOutline,
  IoContractOutline,
  IoDownloadOutline,
  IoExpandOutline,
  IoPencilOutline,
  IoReturnUpForwardOutline,
  IoShareOutline,
  IoTrashOutline,
} from 'react-icons/io5';
import { MdAttachFile } from 'react-icons/md';
import { DocumentPreview } from '../atlas/viewer/AtlasDocumentPanel';
import { FileKindIcon } from '../atlas/viewer/fileIcons';
import { FolderChips, ProcessingPill, VersionBadge } from './FileList';
import FolderSuggestion from './FolderSuggestion';
import {
  formatBytes,
  formatDate,
  kindLabelOf,
  normalizeFileRow,
  processingInfo,
  sourceLabel,
  splitFilename,
  stripOwnExtension,
} from './fileLibrary';

// Clicking through files must not pull big downloads: above these sizes the
// preview waits for "Load preview" (Word/PowerPoint are converted server-side).
export const PREVIEW_AUTO_MAX_BYTES = 15 * 1024 * 1024;
export const CONVERT_AUTO_MAX_BYTES = 5 * 1024 * 1024;

// Fields the metadata endpoint knows better than a list row.
const META_FIELDS = [
  'summary', 'condensed_summary', 'file_category', 'tags', 'key_entities', 'content_type_detail',
  'conversation_id', 'conversation_title', 'root_file_id', 'version_count', 'processing_status',
  'source', 'source_group', 'mime_type', 'size_bytes', 'created_at', 'version_number',
];

function humanize(value) {
  return String(value || '').replace(/[_-]+/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

function entityLabel(entity) {
  if (entity == null) return '';
  if (typeof entity === 'string') return entity;
  return entity.name || entity.value || entity.text || '';
}

const ATTACH_LABELS = {
  attached: 'Added to chat',
  queued: 'Added to chat',
  duplicate: 'In the composer',
};

export default function FileDetailPanel({
  file,
  client,
  revision = 0,
  isMobile,
  attachStatus,
  canShareToCompany,
  onClose,
  onRename,
  onMove,
  onDelete,
  onAttach,
  onShareToCompany,
  onOpenConversation,
  onOpenFolder,
  onDownload,
  suggestionBusy,
  onAcceptSuggestion,
  onDismissSuggestion,
}) {
  const [meta, setMeta] = useState({ status: 'loading', data: null });
  const [versions, setVersions] = useState({ status: 'loading', data: null });
  const [renaming, setRenaming] = useState(null); // { base, ext, busy, error }
  const [expanded, setExpanded] = useState(false);
  const renameInputRef = useRef(null);
  const fileId = file?.file_id;

  useEffect(() => {
    setRenaming(null);
    setExpanded(false);
  }, [fileId]);

  useEffect(() => {
    if (!fileId) return undefined;
    const controller = new AbortController();
    setMeta((prev) => ({ status: prev.data?.file_id === fileId ? 'refreshing' : 'loading', data: prev.data?.file_id === fileId ? prev.data : null }));
    client.metadata(fileId, controller.signal)
      .then((data) => { if (!controller.signal.aborted) setMeta({ status: 'ready', data: { ...data, file_id: fileId } }); })
      .catch(() => { if (!controller.signal.aborted) setMeta({ status: 'error', data: null }); });
    return () => controller.abort();
  }, [client, fileId, revision]);

  useEffect(() => {
    if (!fileId) return undefined;
    const controller = new AbortController();
    setVersions({ status: 'loading', data: null });
    client.versions(fileId, controller.signal)
      .then((data) => { if (!controller.signal.aborted) setVersions({ status: data ? 'ready' : 'unavailable', data }); })
      .catch(() => { if (!controller.signal.aborted) setVersions({ status: 'error', data: null }); });
    return () => controller.abort();
  }, [client, fileId, revision]);

  const isRenaming = Boolean(renaming);
  useEffect(() => {
    if (!isRenaming) return;
    renameInputRef.current?.focus();
    renameInputRef.current?.select();
  }, [isRenaming]);

  if (!file) return null;

  const metaData = meta.data || {};
  const merged = { ...file };
  META_FIELDS.forEach((key) => {
    if (metaData[key] != null && metaData[key] !== '') merged[key] = metaData[key];
  });
  if (Array.isArray(metaData.folders)) {
    merged.folders = normalizeFileRow({ file_id: fileId, folders: metaData.folders }).folders;
  }
  // The row knows the latest name (a rename updates it before metadata refetches).
  merged.filename = file.filename;
  const display = normalizeFileRow(merged);
  const processing = processingInfo(display);
  const summary = display.summary || display.condensed_summary;
  const entities = (Array.isArray(display.key_entities) ? display.key_entities : []).map(entityLabel).filter(Boolean);
  const versionList = Array.isArray(versions.data?.versions) ? versions.data.versions : [];
  const attachLabel = ATTACH_LABELS[attachStatus];

  const startRename = () => {
    const { base, ext } = splitFilename(display.filename);
    setRenaming({ base, ext, busy: false, error: '' });
  };

  const submitRename = async () => {
    if (!renaming || renaming.busy) return;
    // "Report.pdf" typed for a PDF: the extension is kept anyway, don't double it.
    const base = stripOwnExtension(renaming.base, renaming.ext);
    if (!base) {
      setRenaming((prev) => ({ ...prev, error: 'Enter a name.' }));
      return;
    }
    const requested = `${base}${renaming.ext}`;
    if (requested === display.filename) {
      setRenaming(null);
      return;
    }
    setRenaming((prev) => ({ ...prev, busy: true, error: '' }));
    try {
      await onRename(display, requested);
      setRenaming(null);
    } catch (err) {
      setRenaming((prev) => (prev ? { ...prev, busy: false, error: err?.message || 'Rename failed.' } : prev));
    }
  };

  return (
    <aside className={`fl-detail${expanded ? ' is-expanded' : ''}`} aria-label={`Details for ${display.filename}`}>
      <div className="fl-detail-head">
        {isMobile ? (
          <button type="button" className="fl-icon-btn" onClick={onClose} aria-label="Back to files">
            <IoArrowBack size={18} />
          </button>
        ) : null}
        <span className={`fl-detail-icon fl-kind-${display.kind}`}><FileKindIcon file={display} size={20} /></span>
        <div className="fl-detail-title">
          {renaming ? (
            <form
              className="fl-rename"
              onSubmit={(e) => { e.preventDefault(); submitRename(); }}
            >
              <span className="fl-rename-field">
                <input
                  ref={renameInputRef}
                  value={renaming.base}
                  disabled={renaming.busy}
                  onChange={(e) => setRenaming((prev) => ({ ...prev, base: e.target.value, error: '' }))}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      e.preventDefault();
                      setRenaming(null);
                    }
                  }}
                  aria-label="File name"
                  aria-invalid={renaming.error ? 'true' : undefined}
                />
                {renaming.ext ? <span className="fl-rename-ext">{renaming.ext}</span> : null}
              </span>
              <span className="fl-rename-actions">
                <button type="submit" className="fl-btn fl-btn-sm fl-btn-primary" disabled={renaming.busy}>
                  {renaming.busy ? 'Saving…' : 'Save'}
                </button>
                <button type="button" className="fl-btn fl-btn-sm" onClick={() => setRenaming(null)} disabled={renaming.busy}>Cancel</button>
              </span>
              {renaming.error ? <span className="fl-field-error" role="alert">{renaming.error}</span> : null}
            </form>
          ) : (
            <>
              <h3 className="fl-detail-name" title={display.filename}>{display.filename}</h3>
              <div className="fl-detail-sub">
                <span>{kindLabelOf(display)}</span>
                {display.size_bytes != null ? <span>{formatBytes(display.size_bytes)}</span> : null}
                <VersionBadge file={display} />
                <ProcessingPill file={display} />
              </div>
            </>
          )}
        </div>
        {!isMobile ? (
          <>
            <button
              type="button"
              className="fl-icon-btn"
              onClick={() => setExpanded((v) => !v)}
              aria-label={expanded ? 'Smaller preview' : 'Larger preview'}
              title={expanded ? 'Smaller preview' : 'Larger preview'}
            >
              {expanded ? <IoContractOutline size={17} /> : <IoExpandOutline size={17} />}
            </button>
            <button type="button" className="fl-icon-btn" onClick={onClose} aria-label="Close details">
              <IoCloseOutline size={19} />
            </button>
          </>
        ) : null}
      </div>

      <div className="fl-detail-scroll">
        <div className="fl-detail-preview">
          <div className="atlas-doc-body">
            <DocumentPreview
              file={display}
              loadFile={client.loadFile}
              onDownload={onDownload}
              autoLoadMaxBytes={PREVIEW_AUTO_MAX_BYTES}
              autoConvertMaxBytes={CONVERT_AUTO_MAX_BYTES}
            />
          </div>
        </div>

        <div className="fl-detail-actions">
          <button type="button" className="fl-btn fl-btn-sm" onClick={() => onDownload(display)}>
            <IoDownloadOutline size={15} aria-hidden="true" /> Download
          </button>
          {onAttach ? (
            <button
              type="button"
              className={`fl-btn fl-btn-sm${attachLabel ? ' is-done' : ' fl-btn-primary'}`}
              onClick={() => onAttach(display)}
              disabled={processing.busy}
              title={processing.busy ? 'This file is still processing' : attachLabel ? 'In the chat composer. Attach again if you removed it.' : undefined}
            >
              {attachLabel ? <IoCheckmarkCircle size={15} aria-hidden="true" /> : <MdAttachFile size={15} aria-hidden="true" />}
              {attachLabel || 'Attach to chat'}
            </button>
          ) : null}
          <button type="button" className="fl-btn fl-btn-sm" onClick={() => onMove([display])}>
            <IoReturnUpForwardOutline size={15} aria-hidden="true" /> Move to…
          </button>
          <button type="button" className="fl-btn fl-btn-sm" onClick={startRename} disabled={Boolean(renaming)}>
            <IoPencilOutline size={15} aria-hidden="true" /> Rename
          </button>
          {canShareToCompany && onShareToCompany ? (
            <button type="button" className="fl-btn fl-btn-sm" onClick={() => onShareToCompany(display)}>
              <IoShareOutline size={15} aria-hidden="true" /> Send to company
            </button>
          ) : null}
          <button type="button" className="fl-btn fl-btn-sm fl-btn-danger-ghost" onClick={() => onDelete([display])}>
            <IoTrashOutline size={15} aria-hidden="true" /> Delete
          </button>
        </div>

        {onAcceptSuggestion ? (
          <FolderSuggestion
            variant="panel"
            file={display}
            busy={suggestionBusy}
            onAccept={onAcceptSuggestion}
            onDismiss={onDismissSuggestion}
          />
        ) : null}

        <section className="fl-detail-section">
          <h4>Summary</h4>
          {summary ? <p className="fl-detail-summary">{summary}</p> : (
            <p className="fl-muted">
              {meta.status === 'loading' ? 'Loading details…'
                : processing.busy ? 'Atlas is still reading this file.'
                  : 'No summary yet.'}
            </p>
          )}
          {meta.status === 'error' ? <p className="fl-muted">Some details could not be loaded.</p> : null}
        </section>

        {(display.file_category || display.tags.length > 0 || entities.length > 0) ? (
          <section className="fl-detail-section">
            {display.file_category ? (
              <div className="fl-detail-row"><span className="fl-detail-label">Category</span><span>{humanize(display.file_category)}</span></div>
            ) : null}
            {display.tags.length > 0 ? (
              <div className="fl-detail-row">
                <span className="fl-detail-label">Tags</span>
                <span className="fl-chip-wrap">{display.tags.map((t) => <span key={t} className="fl-chip">{t}</span>)}</span>
              </div>
            ) : null}
            {entities.length > 0 ? (
              <div className="fl-detail-row">
                <span className="fl-detail-label">Key entities</span>
                <span className="fl-chip-wrap">{entities.slice(0, 16).map((t) => <span key={t} className="fl-chip fl-chip-muted">{t}</span>)}</span>
              </div>
            ) : null}
          </section>
        ) : null}

        <section className="fl-detail-section">
          <h4>Details</h4>
          <div className="fl-detail-row"><span className="fl-detail-label">Type</span><span>{kindLabelOf(display)}{display.mime_type ? <span className="fl-muted"> · {display.mime_type}</span> : null}</span></div>
          {display.size_bytes != null ? <div className="fl-detail-row"><span className="fl-detail-label">Size</span><span>{formatBytes(display.size_bytes)}</span></div> : null}
          {display.created_at ? <div className="fl-detail-row"><span className="fl-detail-label">Added</span><span>{formatDate(display.created_at, { withTime: true })}</span></div> : null}
          <div className="fl-detail-row"><span className="fl-detail-label">Source</span><span>{sourceLabel(display)}</span></div>
          {display.conversation_id ? (
            <div className="fl-detail-row">
              <span className="fl-detail-label">Chat</span>
              <a
                className="fl-link"
                href={`/c/${display.conversation_id}`}
                onClick={(e) => {
                  if (!onOpenConversation) return;
                  e.preventDefault();
                  onOpenConversation(display.conversation_id);
                }}
              >
                <IoChatbubbleEllipsesOutline size={14} aria-hidden="true" />
                Open the chat it came from
                {display.conversation_title ? <span className="fl-muted"> · {display.conversation_title}</span> : null}
              </a>
            </div>
          ) : null}
          <div className="fl-detail-row">
            <span className="fl-detail-label">Folders</span>
            <span className="fl-chip-wrap"><FolderChips file={display} onOpenFolder={onOpenFolder} max={6} /></span>
          </div>
        </section>

        <section className="fl-detail-section">
          <h4>Versions</h4>
          {versions.status === 'loading' ? <p className="fl-muted">Loading versions…</p> : null}
          {versions.status === 'unavailable' ? <p className="fl-muted">Version history isn&apos;t available yet.</p> : null}
          {versions.status === 'error' ? <p className="fl-muted">Version history could not be loaded.</p> : null}
          {versions.status === 'ready' && versionList.length <= 1 ? <p className="fl-muted">Only one version.</p> : null}
          {versions.status === 'ready' && versionList.length > 1 ? (
            <ul className="fl-versions">
              {versionList.map((v) => {
                const isCurrent = v.is_current_version !== false && (v.is_current_version || v.file_id === display.file_id);
                return (
                  <li key={v.file_id} className={isCurrent ? 'is-current' : ''}>
                    <span className="fl-chip fl-chip-version">v{v.version_number || '?'}</span>
                    <span className="fl-version-meta">
                      <span className="fl-version-name" title={v.filename}>{v.filename}</span>
                      <span className="fl-muted">{[formatDate(v.created_at, { withTime: true }), formatBytes(v.size_bytes)].filter(Boolean).join(' · ')}</span>
                    </span>
                    {isCurrent ? <span className="fl-chip fl-chip-muted">Current</span> : null}
                    <button
                      type="button"
                      className="fl-icon-btn"
                      aria-label={`Download version ${v.version_number}`}
                      title="Download this version"
                      onClick={() => onDownload({ file_id: v.file_id, filename: v.filename })}
                    >
                      <IoDownloadOutline size={16} />
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : null}
        </section>
      </div>
    </aside>
  );
}
